import type { QrPosition, QrSize } from '@wallflower/shared';
import { qrUrl } from '../lib/api.ts';

const POSITION_CLASSES: Record<Exclude<QrPosition, 'hidden'>, string> = {
  'top-left': 'top-[3vh] left-[2.5vw] -rotate-[1.4deg]',
  'top-right': 'top-[3vh] right-[2.5vw] rotate-[1.4deg]',
  'bottom-left': 'bottom-[3vh] left-[2.5vw] rotate-[1.2deg]',
  'bottom-right': 'bottom-[3vh] right-[2.5vw] -rotate-[1.2deg]',
};

/** Bottom corners sit above the message strip rather than under it. */
const LIFTED_CLASSES: Record<Exclude<QrPosition, 'hidden'>, string> = {
  ...POSITION_CLASSES,
  'bottom-left': 'bottom-[10vh] left-[2.5vw] rotate-[1.2deg]',
  'bottom-right': 'bottom-[10vh] right-[2.5vw] -rotate-[1.2deg]',
};

/** Sized in viewport width so the card scales with the TV, not the browser font. */
const CARD_WIDTH: Record<QrSize, string> = {
  sm: '13vw',
  md: '16vw',
  lg: '21vw',
};

interface Props {
  sessionId: string;
  position: QrPosition;
  size: QrSize;
  prompt: string;
  /** Raise a bottom-corner card so the message strip doesn't run under it. */
  liftAboveTicker?: boolean;
}

/**
 * A printed place card, the kind that sits on an event table - warm stock,
 * slightly off-axis, resting on the slideshow rather than floating over it.
 */
export default function QrPlaceCard({
  sessionId,
  position,
  size,
  prompt,
  liftAboveTicker = false,
}: Props) {
  if (position === 'hidden') return null;

  const placement = liftAboveTicker ? LIFTED_CLASSES[position] : POSITION_CLASSES[position];

  return (
    <div
      className={`place-card absolute z-20 flex flex-col items-center ${placement}`}
      style={{ width: CARD_WIDTH[size], padding: '1.1vw 1.1vw 0.9vw' }}
    >
      <img
        src={qrUrl(sessionId, size)}
        alt={`QR code to upload photos: ${prompt}`}
        className="w-full rounded-[3px] bg-white"
        draggable={false}
      />
      <p
        className="mt-[0.7vw] text-center leading-[1.15] font-semibold"
        style={{ fontFamily: 'var(--font-display)', fontSize: `calc(${CARD_WIDTH[size]} * 0.098)` }}
      >
        {prompt}
      </p>
      {/* Hairline rule and mark, like the printer's flourish on a real card. */}
      <div
        className="mt-[0.5vw] h-px w-1/3"
        style={{ background: 'rgba(34,28,23,0.28)' }}
        aria-hidden
      />
      <p
        className="mt-[0.35vw] tracking-[0.22em] uppercase opacity-55"
        style={{ fontFamily: 'var(--font-display)', fontSize: `calc(${CARD_WIDTH[size]} * 0.045)` }}
      >
        Wallfeed.uk
      </p>
    </div>
  );
}
